// Frontend Configuration
// This file contains only frontend-specific configuration
// All external service calls (OpenAI, etc.) go through the backend

// Environment Detection
export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
export const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// Supabase Configuration - Only from environment variables
export const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://ivqcfrhrkzfaeizwmyxc.supabase.co';
export const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const SUPABASE_BUCKET = process.env.REACT_APP_SUPABASE_BUCKET || 'uploads';

// Frontend App Configuration
export const APP_NAME = 'Learning App';
export const APP_VERSION = '1.0.0';
export const APP_DESCRIPTION = 'AI-powered learning platform';

// UI Configuration
export const THEME = {
  primary: '#3B82F6',
  secondary: '#10B981',
  accent: '#F59E0B',
  background: '#FFFFFF',
  text: '#1F2937'
};

// Feature Flags
export const FEATURES = {
  DARK_MODE: true,
  NOTIFICATIONS: true,
  OFFLINE_SUPPORT: false,
  MULTI_LANGUAGE: false
};

// Local Storage Keys
export const STORAGE_KEYS = {
  USER_PREFERENCES: 'user_preferences',
  THEME_SETTING: 'theme_setting',
  LANGUAGE: 'language',
  NOTIFICATIONS: 'notifications'
};

// API Configuration (for any remaining frontend API calls)
export const API_CONFIG = {
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000
};

// Backend API URL - for any remaining frontend API calls
export const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL || (IS_DEVELOPMENT ? 'http://localhost:8000' : 'https://apis.knoowl.in');

// Legacy API URL for backward compatibility
export const API_URL = BACKEND_API_URL;

// API Key for backend authentication (if needed)
export const API_KEY = process.env.REACT_APP_API_KEY || 'test-key';

// Validation Configuration
export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png']
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  AUTH_ERROR: 'Authentication failed. Please log in again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  FILE_TOO_LARGE: 'File size exceeds the maximum limit.',
  INVALID_FILE_TYPE: 'File type not supported.'
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Successfully logged in!',
  LOGOUT_SUCCESS: 'Successfully logged out!',
  UPLOAD_SUCCESS: 'File uploaded successfully!',
  SAVE_SUCCESS: 'Changes saved successfully!'
};

// Check if we're in production and missing critical config
if (!IS_DEVELOPMENT && !SUPABASE_ANON_KEY) {
  console.error('🚨 CRITICAL: Missing SUPABASE_ANON_KEY in production!');
  console.error('Please set REACT_APP_SUPABASE_ANON_KEY in your Vercel environment variables.');
  console.error('Go to: Vercel Dashboard → Your Project → Settings → Environment Variables');
}

// Validate required environment variables (only Supabase for frontend)
const requiredEnvVars = [
  'REACT_APP_SUPABASE_URL',
  'REACT_APP_SUPABASE_ANON_KEY'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

if (missingVars.length > 0) {
  console.error('❌ Missing required environment variables:', missingVars);
  console.error('Please set these environment variables in your deployment.');
  
  if (IS_DEVELOPMENT) {
    console.error('🔧 For local development, create a .env file with these variables');
    console.error('🔧 Copy env.template to .env and fill in your Supabase credentials');
  }
  
  throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
}

// Log configuration in development
if (IS_DEVELOPMENT) {
  console.log('🔧 Frontend Configuration Loaded:');
  console.log('Environment:', process.env.NODE_ENV);
  console.log('Supabase URL:', SUPABASE_URL);
  console.log('Supabase Bucket:', SUPABASE_BUCKET);
  console.log('Features Enabled:', Object.keys(FEATURES).filter(key => FEATURES[key]));
}

// RAG Configuration Settings (for UI display only - actual processing happens in backend)
export const RAG_CONFIG = {
  // Embedding Model (for document processing)
  embedding_model: process.env.REACT_APP_EMBEDDING_MODEL || 'text-embedding-ada-002',
  
  // Generation Model (for AI responses)
  generation_model: process.env.REACT_APP_GENERATION_MODEL || 'gpt-4o-mini',
  
  // RAG Parameters
  chunk_size: process.env.REACT_APP_CHUNK_SIZE || 'semantic',
  top_k_chunks: parseInt(process.env.REACT_APP_TOP_K_CHUNKS) || 5,
  max_tokens: parseInt(process.env.REACT_APP_MAX_TOKENS) || 1000,
  similarity_threshold: parseFloat(process.env.REACT_APP_SIMILARITY_THRESHOLD) || 0.7
};

// Available model options for UI selection
export const AVAILABLE_EMBEDDING_MODELS = [
  { value: 'text-embedding-ada-002', label: 'text-embedding-ada-002 (Default)', dimensions: 1536 },
  { value: 'text-embedding-3-small', label: 'text-embedding-3-small', dimensions: 1536 },
  { value: 'text-embedding-3-large', label: 'text-embedding-3-large', dimensions: 3072 }
];

export const AVAILABLE_GENERATION_MODELS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Default)', cost: 'Low' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', cost: 'Very Low' },
  { value: 'gpt-4o', label: 'GPT-4o', cost: 'Medium' },
  { value: 'claude-3-haiku', label: 'Claude 3 Haiku', cost: 'Low' }
];

// Note: OpenAI API calls are handled by the backend, not the frontend
// export const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY; 